import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { PreviewResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { FC, useEffect, useState } from "react";
import { Box, Checkbox, Stack, Textarea } from "shared/components/Spor";

export type DropsLogTextInputProps = {
  dropsLogText: string | null;
  setDropsLogText: (text: string | null) => void;
  uuid: string;
  defaultEnabled?: boolean;
};

export const DropsLogTextInput: FC<DropsLogTextInputProps> = ({
  dropsLogText,
  setDropsLogText,
  uuid,
  defaultEnabled = false,
}) => {
  const [dropsLogTextEnabled, setDropsLogTextEnabled] =
    useState(defaultEnabled);
  const [previewText, setPreviewText] = useState<string | null>(null);

  const toggleDropsLogText = () => {
    if (dropsLogTextEnabled) {
      // Wipe text when setting enabled to false so we don't accidentally send it anyway
      setDropsLogText(null);
    }
    setDropsLogTextEnabled(!dropsLogTextEnabled);
  };

  const { mutate: getPreviewData } = useMutation<PreviewResponse>({
    mutationKey: ["closeTrainPreview", uuid],
    mutationFn: () =>
      mutationFnPOST<PreviewResponse, undefined>(
        `${getBackendUrl()}/trainInformation/preview/close/${uuid}`,
      ),
    onSuccess: (data) => {
      // Save the preview text so we can autofill with it when text is enabled
      setPreviewText(data.internalMessage);
    },
    onError: () => {
      // Remove any previous preview texts to avoid stale preview.
      // If dropsLogText hasn't been manually changed, remove it too.
      if (dropsLogText === previewText) {
        setDropsLogText(null);
      }
      setPreviewText(null);
    },
  });

  useEffect(() => {
    // Fetch preview data once when uuid changes
    getPreviewData();
  }, [uuid]);

  useEffect(() => {
    // If dropsLogText is enabled, autofill with preview text
    // whenever one exists and the text field is empty.
    if (dropsLogTextEnabled && !dropsLogText && previewText) {
      setDropsLogText(previewText);
    }
  }, [dropsLogText, previewText, dropsLogTextEnabled]);

  return (
    <Stack gap="12px">
      <Checkbox
        checked={dropsLogTextEnabled}
        onCheckedChange={toggleDropsLogText}
      >
        Send melding til Operativ logg
      </Checkbox>
      <Box p="3px" /* Because of textarea outline */>
        {dropsLogTextEnabled && (
          <Textarea
            label="Melding"
            value={dropsLogText ?? undefined}
            onChange={(e) => {
              setDropsLogText(e.target.value);
            }}
          />
        )}
      </Box>
    </Stack>
  );
};
